import React, { useMemo, useState } from "react";
import { DataGridColumnConfig, DataGridFilterType } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFilter } from "./useFilter";
import { Button } from "components/Button";
import { DataGridFilterChips } from "./DataGridFilterChips";
import useMediaQuery from "components/utils/useMediaQuery";
import { DataGridFilterRowHeader } from "./DataGridFilterRow";
import classNames from "classnames";
import { isEmpty } from "lodash";

export const DataGridFilterHeaders: React.FC<{
    columns: DataGridColumnConfig[];
    canSelectRows?: boolean;
    isFilterOpen?: boolean;
    isSortOpen?: boolean;
    isFilterEmpty: boolean;
    toggleFilter: (state?: boolean) => void;
    toggleSort: (state?: boolean) => void;
    filter: DataGridFilterType;
    sortBy?: string;
    sortAsc?: boolean;
    totalRecords?: number;
    totalSelectedRecords?: number;
    SelectedActionsRowComponent?: JSX.Element | null;
    getFilterValue: ReturnType<typeof useFilter>["getFilterValue"];
    onFilterClear: () => void;
    onSortClear: () => void;
    onFilterSubmit: (filterValue?: DataGridFilterType) => void;
    onFieldClean: (key: string) => void;
    onFilterInputFocus: (key: string) => void;
    onSortChange: (key: string) => void;
    handleSelectAllRows?: (forceUnselectAll?: boolean) => void;
}> = ({
    columns,
    canSelectRows = false,
    filter,
    sortBy,
    sortAsc,
    isFilterOpen = false,
    isSortOpen = false,
    isFilterEmpty,
    toggleFilter,
    toggleSort,
    totalRecords = 0,
    totalSelectedRecords = 0,
    SelectedActionsRowComponent,
    getFilterValue,
    onFilterClear,
    onSortClear,
    onFilterSubmit,
    onFieldClean,
    onFilterInputFocus,
    onSortChange,
    handleSelectAllRows,
}) => {
    const totalColumns = useMemo(() => columns.length + (canSelectRows ? 1 : 0), [columns.length, canSelectRows]);
    const [showFilterSetting, setShowFilterSetting] = useState(false);
    const isMobile = useMediaQuery("(max-width: 768px)");

    const toggleFilterSetting = () => {
        setShowFilterSetting(!showFilterSetting);
    };

    return (
        (Object.keys(filter).length > 0 || totalSelectedRecords > 0 || isMobile) && (
            <>
                {(!isFilterOpen || !isSortOpen || totalSelectedRecords > 0 || isMobile) && (
                    <>
                        <tr className="d-table-row sticky-top">
                            <th
                                style={totalSelectedRecords > 0 ? { backgroundColor: "var(--bg-hover)" } : {}}
                                className={classNames("border border-top-0 border-start-0 border-end-0", {
                                    "rounded-0": isMobile,
                                })}
                                colSpan={isMobile ? 1 : totalColumns + 1}
                            >
                                <div className="d-flex gap-2 align-items-center">
                                    {totalSelectedRecords > 0 && (
                                        <>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => handleSelectAllRows && handleSelectAllRows(true)}
                                                className={classNames("d-flex align-items-center gap-1 border-0 flex-shrink-0", {
                                                    "p-0 pe-2": isMobile,
                                                    "py-1 px-2": !isMobile,
                                                })}
                                                aria-label="Unselect all"
                                            >
                                                <span className="fs-8 badge bg-primary">{totalSelectedRecords}</span>
                                                <span className="text-black">Selected</span>
                                                <FontAwesomeIcon className="visible" icon={"xmark"} />
                                            </Button>

                                            <span style={{ color: "var(--bs-border-color)" }} className="pe-1 fw-lighter">|</span>

                                            {SelectedActionsRowComponent}
                                        </>
                                    )}
                                    {totalSelectedRecords === 0 && Object.keys(filter).length > 0 && !isMobile && (
                                        <div className="d-flex pe-2 gap-2">
                                            <span className="fs-8 badge bg-primary">{totalRecords}</span>Results
                                        </div>
                                    )}

                                    {totalSelectedRecords === 0 && (!isEmpty(filter) || isMobile) && (
                                        <DataGridFilterRowHeader
                                            columns={columns}
                                            filter={filter}
                                            sortBy={sortBy}
                                            sortAsc={sortAsc}
                                            toggleFilter={toggleFilter}
                                            toggleSort={toggleSort}
                                            onFilterInputFocus={onFilterInputFocus}
                                            onFieldClean={onFieldClean}
                                            toggleFilterSetting={toggleFilterSetting}
                                            isFilterEmpty={isFilterEmpty}
                                            onFilterClear={onFilterClear}
                                        />
                                    )}
                                </div>
                            </th>
                        </tr>
                    </>
                )}
                {showFilterSetting && (
                    <DataGridFilterChips
                        className="d-lg-none m-3"
                        filter={filter}
                        columns={columns}
                        sortBy={sortBy}
                        sortAsc={sortAsc}
                        formTitle="Filters"
                        onFilterClear={onFilterClear}
                        onSortClear={onSortClear}
                        onFilterSubmit={onFilterSubmit}
                        onSortChange={onSortChange}
                        getFilterValue={getFilterValue}
                        toggleFilter={toggleFilterSetting}
                        showFilter={showFilterSetting}
                    />
                )}
            </>
        )
    );
};
